import moment, { Moment } from "moment";
import React from "react";
import { CLAIMS_ENTITY, FEATURES } from "../../lib/claims";
import context from "../../lib/context";
import security from "../../lib/security";
import util from "../../lib/util";
import viewModelApi from "../../lib/view-model-api";
import LoadingPanel from "../shared/loading-panel";

import PtoCalendarViewDatePicker from "./_components/pto-calendar-view-date-picker";
import PtoCalendarViewOptions from "./_components/pto-calendar-view-options";
import PtoCalendarViewLegend from "./_components/pto-calendar-view-legend";
import PtoCalendarViewReportTree from "./_components/pto-calendar-view-report-tree";
import PtoCalendar from "./_components/pto-calendar";
import { PTO_LEGEND_ARRAY, VIEW_MODE, VIEW_STATUS } from "./_components/_common";
import PtoDetail from "./pto-detail";
import { Modal } from "react-bootstrap";
import ajax from "../../lib/ajax";

/**
 * @typedef {{
 *  startDate: Moment,
 *  endDate: Moment
 * }} DateRange
 * 
 */


export default class PTOCalendarView extends React.Component {
    constructor(props) {
        super(props);


        this._reloadCalendar = this._reloadCalendar.bind(this);
        this._renderCalendar = this._renderCalendar.bind(this);
        this._onSelectEmployee = this._onSelectEmployee.bind(this);
        this._flattenEmployeeTree = this._flattenEmployeeTree.bind(this);
        this._getSelectedEmployees = this._getSelectedEmployees.bind(this);
        this._onApprove = this._onApprove.bind(this);

        const curWeek = util.getCurrentWeekRange();

        this.state = {
            viewStatus: VIEW_STATUS.SecurityCheck,
            errorMessage: "",

            viewSettings: {
                viewMode: VIEW_MODE.WEEK,
                showWeekend: true,
                showStaffWithLeaveOnly: false,
                supervisorView: false
            },

            selectedReportingItemKey: null,
            selectedEmployees: [],
            cache: new Map(),
            vm: null,

            view: {
                pickerOpen: true,
                defaultWeek: curWeek,
                selectedDate: curWeek.startDate,
                selectedDays: util.getAllDaysInRange(curWeek.startDate, curWeek.endDate).map(v => v.toDate())
            },

            detailDialog: {
                selectedId: 0,
                action: "",
                selectedType: "",
                dialogTitle: "",
                showDialog: false
            },

            deniable: new Map()
        }

        this.calendarRef = React.createRef(null);
    }

    componentDidMount() {
        if (this.state.viewStatus === VIEW_STATUS.SecurityCheck) {
            Promise.all([this._initializeAsync()])
                .then(() => {
                    if (this.props.onLoaded) this.props.onLoaded();
                });
        }
    }

    _reloadCalendar() {
        if (this.calendarRef.current) this.calendarRef.current.reload();
    }

    async _initializeAsync() {
        try {
            const res = await security.assertAccessToEntity(CLAIMS_ENTITY.Security_EntityType_Features, FEATURES.PTO_Calendar_View)
            if (!!res.data.success) {
                this.setState({
                    viewStatus: VIEW_STATUS.LoadingData
                });

                const vm = await viewModelApi.loadPTOCalendarViewModelAsync();

                /** @type {[{typeCode : string, reasonCode: string}]} */
                const deniable = (await ajax.api().get('get-pto-deniable')).data;
                const deniableMap = new Map();
                deniable.forEach(item => deniableMap.set(`${item.typeCode}|${item.reasonCode}`, item));

                const cache = this._flattenEmployeeTree(vm.employeeTree);
                this.setState({
                    viewStatus: VIEW_STATUS.Loaded,
                    vm,
                    selectedEmployees: this._getSelectedEmployees(null, vm.employeeTree || [], cache),
                    cache,
                    deniable: deniableMap
                });
            } else {
                this.setState({
                    viewStatus: VIEW_STATUS.Error,
                    errorMessage: "Access Denied"
                });
            }
        } catch (err) {
            console.error(err);
            this.setState({
                viewStatus: VIEW_STATUS.Error,
                errorMessage: "Error while loading. Please contact helpdesk for support."
            });
        }
    }

    _flattenEmployeeTree(employees) {
        const map = new Map();
        for (const e of employees) {
            map.set(e.peopleId, e);
            if (e.directReportingStaffs && e.directReportingStaffs.length) {
                const m = this._flattenEmployeeTree(e.directReportingStaffs);
                for (const k of m.keys()) map.set(k, m.get(k));
            }
        }
        return map;
    }

    _getSelectedEmployees(peopleId, tree, cache) {
        if (peopleId) {
            const result = [];
            const supervisor = cache.get(peopleId);
            result.push(supervisor);
            if (supervisor.directReportingStaffs) for (const emp of supervisor.directReportingStaffs) result.push(emp);
            return result;
        } else {
            return tree ? [...(tree)] : [];
        }
    }

    _onSelectEmployee(selectedPeopleTO) {
        this.setState({
            selectedReportingItemKey: selectedPeopleTO ? selectedPeopleTO.peopleId : null,
            selectedEmployees: this._getSelectedEmployees(selectedPeopleTO ? selectedPeopleTO.peopleId : null, this.state.vm.employeeTree || [], this.state.cache),
            viewSettings: {
                ...(this.state.viewSettings),
                supervisorView: selectedPeopleTO && selectedPeopleTO.directReportingStaffs && selectedPeopleTO.directReportingStaffs.length > 0
            }
        })
    }

    _onSelectedDaysChange(selectedDays) {
        this.setState({
            view: {
                ...this.state.view,
                selectedDate: moment(selectedDays[0]),
                selectedDays
            }
        })
    }

    _onViewSettingsChange(newSettings) {

        this.setState({
            viewSettings: {
                ...(this.state.viewSettings),
                viewMode: newSettings.view,
                showWeekend: newSettings.viewOption.showWeekend,
                showStaffWithLeaveOnly: newSettings.viewOption.showOnlyStaffWithLeave,
            }
        });
    }

    _onApprove(ptoId) {
        const self = this;
        context.message({
            show: true,
            type: "loading",
            message: "Approving request. Please wait..."
        });

        return ajax.api()
            .post('update-pto', { ptoId: ptoId, action: 'approve' })
            .then(function (res) {
                context.message({
                    show: false
                });
                self._reloadCalendar();
            })
            .catch(function (err) {
                const message = err.response.data.exceptionMessage || err.response.data.message;
                context.message({
                    show: true,
                    type: "error",
                    message: message,
                    buttons: "close"
                });
            });
    }

    _renderCalendar() {
        const state = this.state;
        const dateRange = state.viewSettings.viewMode === VIEW_MODE.WEEK ?
            util.getWeekRangeOf(state.view.selectedDate) :
            util.getMonthRangeOf(state.view.selectedDate);

        const key = `${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}_${state.selectedEmployees.map(v => v.peopleId).join("|")}`;

        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ap-horiz-flex-container">
                            <div className="ap-horiz-flex-container" style={{ flexGrow: 1 }}>
                                <PtoCalendarViewDatePicker value={state.view.selectedDate}
                                    key={`picker_${state.viewSettings.viewMode}`}
                                    selectionMode={state.viewSettings.viewMode}
                                    onChange={this._onSelectedDaysChange.bind(this)} />
                                <PtoCalendarViewOptions onChange={this._onViewSettingsChange.bind(this)} />
                                <PtoCalendarViewLegend items={PTO_LEGEND_ARRAY} />
                            </div>
                            <div style={{ flexGrow: 0 }}>
                                <PtoCalendarViewReportTree staffTree={this.state.vm.employeeTree} onChange={this._onSelectEmployee} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <PtoCalendar ref={this.calendarRef} key={key}
                            deniableCodes={this.state.deniable}
                            dateRange={dateRange} viewSettings={state.viewSettings} employees={state.selectedEmployees}
                            onApprove={(item) => this._onApprove(item.pto.ptoId)}
                            onDeny={(item) => this._openDetail(item.pto.ptoId, "deny", item.typeCode)}
                            onConsultHR={(item) => this._openDetail(item.pto.ptoId, "consult")}
                            onEdit={(item) => this._openDetail(item.pto.ptoId, "edit")}
                            onCancel={(item) => this._openDetail(item.pto.ptoId, "cancel")} />
                    </div>
                </div>

            </div>
        );
    }

    _openDetail(ptoId, action, type) {
        //onDeny={} onConsultHR={} onCancel={}
        const clone = { ...this.state.detailDialog };

        clone.selectedId = ptoId;
        clone.showDialog = true;
        clone.action = action;

        switch (action) {
            case "create":
                clone.dialogTitle = "Request Time-Off ";
                clone.selectedType = type ? type : ""; // can default Type only when creation
                break;
            case "edit":
                clone.dialogTitle = "Edit Time-Off Request";
                break;
            // PAP-397 - Supervisor notes is NOT a required field when approve a TO request (just approve right away)
            // case "approve":
            //     clone.dialogTitle = "Approve Time-Off";
            //     break;
            case "deny":
                clone.selectedType = type ? type : "";
                clone.dialogTitle = "Decline " + (clone.selectedType.includes("FTO") ? "FTO" : "Time-Off");
                break;
            case "consult":
                clone.dialogTitle = "Consult HR";
                break;
            case "cancel":
                clone.dialogTitle = "Cancel Time-Off Request";
                break;
        }
        this.setState({
            detailDialog: clone
        });
    }

    _closeDetail() {
        const clone = { ...this.state.detailDialog };
        clone.showDialog = false;
        this.setState({ detailDialog: clone });
    }

    _renderLoaded() {

        const currWeekRange = util.getCurrentWeekRange();

        const dlgDetail = <Modal className="ap-pto-dialog" show={this.state.detailDialog.showDialog} onHide={() => this._closeDetail()} backdrop="static">
            <Modal.Header closeButton={true}>
                <Modal.Title className="ap-pto-dialog-title">
                    <i className="fa fa-calendar-o" /> {this.state.detailDialog.dialogTitle}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <PtoDetail ptoId={this.state.detailDialog.selectedId} onSaved={() => {
                    this._closeDetail();
                    this._reloadCalendar();
                }}
                    onClosed={() => this._closeDetail()}
                    userRole="Supervisor" action={this.state.detailDialog.action} canBeDenied={null}
                    switchToApproveOrDeny={(action) => this.openDetail(this.state.detailDialog.selectedId, action)}
                    defaultType={this.state.detailDialog.selectedType} />
            </Modal.Body>
        </Modal>;



        const calendar = this._renderCalendar();

        return <div>
            {calendar}
            {dlgDetail}
        </div>;
    }

    _renderError() {
        return (
            <div className="alert alert-danger text-left">
                {this.state.errorMessage}
            </div>
        );
    }

    _renderLoading() {
        return (
            <div className="text-center">
                <div>
                    <LoadingPanel message="Loading. Please wait..." />
                </div>
            </div>
        );
    }

    render() {

        switch (this.state.viewStatus) {
            case VIEW_STATUS.Loaded:
                return this._renderLoaded();
            case VIEW_STATUS.Error:
                return this._renderError();
            default:
                return this._renderLoading();
        }
    }
}